// @ts-check
/* eslint camelcase: 0, no-console: 0, no-unused-vars: 0 */
/* globals _, Backbone, ga, mixpanel, mp  */

/**
 * @typedef {import("common/types/reports/messages").BehaviorSelector} BehaviorSelector
 * @typedef {import("common/types/reports/messages").UserSelector} UserSelector
 * @typedef {import("common/types/reports/messages").PeoplePropertySelector} PeoplePropertySelector
 */

import onDocumentReady from 'document-ready';
import isNil from 'lodash/isNil';

import * as displayNameRegistry from 'common/report/display-name-registry';
import {parseUri} from 'common/util';
import {arbEscape} from 'common/report/util/arb';
import {fetchEventDefinitions, fetchPropertyDefinitions} from 'common/util/definitions';
import {getTooltipDataForEvent, getTooltipDataForProperty} from 'common/widgets/context-panel/util';
import {apiClient} from 'common/util/api-client';

import {isValidEmail} from './utils/string';
import {getCountryDropdownOptions} from './utils/geo';

/**
 * Returns a string or an object based on existence of behaviors
 *
 * @param {PeoplePropertySelector} selector
 * @param {BehaviorSelector} behaviors
 * @returns {UserSelector}
 */
export function build_selector(selector, behaviors) {
  let ret;
  if (!_.isEmpty(behaviors)) {
    ret = {
      selector,
      behaviors,
    };
  } else {
    ret = selector;
  }
  return ret;
}

/**
 * Selectors can either be a string or an object containing
 * the keys selector, behaviors.  This method returns an object
 * with a selector key and a behavior key if available in original.
 *
 * @param {UserSelector} selector
 * @returns {{selector: PeoplePropertySelector, behaviors?: BehaviorSelector}}
 */
export function parse_selector(selector) {
  const to = {};

  if (selector !== null && selector !== undefined) {
    if (typeof selector === `object`) {
      if (!_.isEmpty(selector.behaviors)) {
        to.behaviors = selector.behaviors;
      }
      if (selector.selector) {
        to.selector = selector.selector;
      }
    } else {
      to.selector = selector;
    }
  }

  return to;
}

const is_campaign_event = (name) =>
  name && name.match(/^\$campaign_|^\$message_suppressed$|^\$journey_entered$|^\$unsubscribe$/);
const is_experiment_event = (name) => name === `$experiment_started`;

const includes_event_type = function (events, event_predicate) {
  let global_custom_events;
  try {
    global_custom_events = Backbone.data.custom_events;
  } catch (err) {
    console.error(err);
  }

  const self_or_non_custom_alternatives = (name) => {
    let alternatives;
    if (global_custom_events) {
      const custom_event = global_custom_events.get_by_name(name);
      if (custom_event) {
        alternatives = custom_event.get_alternatives();
      }
    }

    return alternatives || name;
  };

  return _.chain(events) // wrap for clarity
    .map(self_or_non_custom_alternatives)
    .flatten()
    .any(event_predicate)
    .value();
};

export function includes_campaign_event(events) {
  return includes_event_type(events, is_campaign_event);
}

export function rename_mp_property(property_name, event) {
  const mapping = mp.report.globals.mapped_properties;
  if (Object.prototype.hasOwnProperty.call(mapping, property_name) && !isNil(mapping[property_name])) {
    return mapping[property_name];
  }

  if (event && property_name === `campaign_id` && includes_campaign_event([event])) {
    return `Campaign`;
  }

  // map default properties from collect everything events
  const cePrefixMap = {
    $el_classes: `Element css classes`,
    $el_tag_name: `Element tag`,
    $el_text: `Element text`,
    $el_value: `Element field value`,
    $el_attr__href: `Element URL`,
    '$el_attr__data-': `Element data: `,
    $el_attr__: `Element attribute: `,
    $form_field__: `Form field: `,
  };
  for (const prefix in cePrefixMap) {
    if (property_name.substring && property_name.substring(0, prefix.length) === prefix) {
      return cePrefixMap[prefix] + property_name.substring(prefix.length);
    }
  }

  // default conversion for all properties starting with '$'
  if (property_name[0] === `$` && property_name.length > 1) {
    const parts = _.map(property_name.substr(1).split(`_`), _.capitalize);
    return parts.join(` `);
  }

  return property_name;
}

export function pretty_file_size(bytes, decimals = 0) {
  if (bytes === 0) return `0 Bytes`;

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = [`Bytes`, `KB`, `MB`, `GB`, `TB`, `PB`, `EB`, `ZB`, `YB`];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ` ` + sizes[i];
}

const tld_regex = new RegExp(
  `\\.(aero|biz|cat|com|coop|edu|gov|info|int|jobs|mil|mobi|museum|` +
    `name|net|org|travel|ac|ad|ae|af|ag|ai|al|am|an|ao|aq|ar|as|at|au|aw|az|` +
    `ba|bb|bd|be|bf|bg|bh|bi|bj|bm|bn|bo|br|bs|bt|bv|bw|by|bz|ca|cc|cd|cf|` +
    `cg|ch|ci|ck|cl|cm|cn|co|cr|cs|cu|cv|cx|cy|cz|de|dj|dk|dm|do|dz|ec|ee|` +
    `eg|eh|er|es|et|eu|fi|fj|fk|fm|fo|fr|ga|gb|gd|ge|gf|gg|gh|gi|gl|gm|gn|` +
    `gp|gq|gr|gs|gt|gu|gw|gy|hk|hm|hn|hr|ht|hu|id|ie|il|im|in|io|iq|ir|is|` +
    `it|je|jm|jo|jp|ke|kg|kh|ki|km|kn|kp|kr|kw|ky|kz|la|lb|lc|li|lk|lr|ls|` +
    `lt|lu|lv|ly|ma|mc|md|mg|mh|mk|ml|mm|mn|mo|mp|mq|mr|ms|mt|mu|mv|mw|mx|` +
    `my|mz|na|nc|ne|nf|ng|ni|nl|no|np|nr|nu|nz|om|pa|pe|pf|pg|ph|pk|pl|pm|` +
    `pn|pr|ps|pt|pw|py|qa|re|ro|ru|rw|sa|sb|sc|sd|se|sg|sh|si|sj|sk|sl|sm|` +
    `sn|so|sr|st|su|sv|sy|sz|tc|td|tf|tg|th|tj|tk|tm|tn|to|tp|tr|tt|tv|tw|` +
    `tz|ua|ug|uk|um|us|uy|uz|va|vc|ve|vg|vi|vn|vu|wf|ws|ye|yt|yu|za|zm|zr|` +
    `zw)\\b`,
);

export function isURL(str) {
  if (typeof str !== `string` || str.indexOf(` `) !== -1) {
    return false;
  }
  const has_httpish_protocol = /^https?:\/\//.test(str);
  const has_ftp_protocol = str.indexOf(`ftp://`) === 0;
  const maybe_email = !has_httpish_protocol && !has_ftp_protocol && str.indexOf(`@`) !== -1;
  const maybe_url = has_httpish_protocol || tld_regex.test(str);
  return maybe_url && !maybe_email && !has_ftp_protocol;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
_.namespace(`mp.utility`, function (exports) {
  'use strict';

  const should_feature_gate_date_range_for_report = function () {
    return [`segmentation`, `funnels`, `retention`, `formulas`].indexOf(mp.report.globals.report_name) > -1;
  };
  exports.maybe_add_feature_gate_upsell_to_calendar = function (container) {
    if (mp.report.globals.feature_gate_name === `free` && should_feature_gate_date_range_for_report()) {
      const max_data_history_days = mp.report.globals.feature_gates.max_data_history_days;
      const upsell_text = $(`<div>`)
        .addClass(`text`)
        .html(
          `Analyze your historical data beyond ` +
            max_data_history_days +
            ` days. <a class="upgrade-link" href="/pricing" target="_blank">Upgrade</a> your plan!`,
        );
      const upsell_icon = `<img class="lightning-bolt" src="/site_media/images/pricing/lightning-bolt.svg">`;
      const contents = $(`<div>`).addClass(`contents`).append(upsell_icon).append(upsell_text);
      const upsell = $(`<div>`).addClass(`datepicker-upsell`).append(contents);
      container.prepend(upsell);
    }
  };

  exports.track_ga = function (...args) {
    try {
      // @ts-ignore - ga is global
      ga(...args);
    } catch (err) {
      if (err instanceof ReferenceError) {
        // GOOT FOR SRFE-1717 and SRFE-1682
      }
    }
  };

  exports.feature_gate_datepicker = function (date) {
    // Feature gating
    let css_class = ``;
    const max_days_ago_date = mp.utility.get_oldest_allowed_date_from_feature_gate_set();
    const is_allowed = date >= max_days_ago_date;
    if (!is_allowed && should_feature_gate_date_range_for_report()) {
      css_class = `feature-gated`;
    }
    return [is_allowed, css_class];
  };

  exports.get_oldest_allowed_date_from_feature_gate_set = function () {
    const max_days_ago_date = new Date();
    if (mp.report.globals.feature_gate_name === `legacy` || !should_feature_gate_date_range_for_report()) {
      // TODO: refactor to not use datejs which overrides native Date
      // @ts-ignore 'addYears' does not exist on type 'Date'.
      max_days_ago_date.addYears(-100);
      return max_days_ago_date;
    }
    const max_days_ago = mp.report.globals.feature_gates.max_data_history_days;
    max_days_ago_date.setHours(0);
    max_days_ago_date.setMinutes(0);
    max_days_ago_date.setSeconds(0);
    max_days_ago_date.setMilliseconds(0);
    // TODO: refactor to not use datejs which overrides native Date
    // @ts-ignore 'addDays' does not exist on type 'Date'.
    max_days_ago_date.addDays(-max_days_ago);
    if (mp.report.globals.report_name === `retention` && max_days_ago >= 365) {
      max_days_ago_date.setDate(1);
    }
    return max_days_ago_date;
  };

  // Bind esc key press to a handler
  exports.bind_esc = function (namespace, handler, context) {
    if (context) {
      handler = _.bind(handler, context);
    }

    $(`body`).on(`keydown.` + namespace, function (ev) {
      if (ev.which === 27) {
        handler(ev);
        $(`body`).off(`keydown.` + namespace);
      }
    });
  };

  exports.unbind_esc = function (namespace, handler) {
    if (handler) {
      $(`body`).off(`keydown.` + namespace, handler);
    } else {
      $(`body`).off(`keydown.` + namespace);
    }
  };

  // Bind an outside event that only calls the handler if the event doesn't occur
  // on jquery_obj or one of its children.
  //
  // NOTE: unbind_outside_event must be called to unbind the event; it will cause a
  // memory leak if it isn't
  exports.bind_outside_event = function (event_name, namespace, jquery_obj, handler, context) {
    // jshint ignore:line
    if (context) {
      handler = _.bind(handler, context);
    }

    $(document).on(event_name + `.` + namespace + `.` + `OUTSIDE_EVENT`, function (ev) {
      // @ts-ignore
      // prettier-ignore
      if (jquery_obj.length && !$(jquery_obj).is(ev.target) && !$.contains(jquery_obj[0], ev.target) && $.contains(document, ev.target)) {
        handler(ev);
      }
    });
  };

  // Unbind an outside event
  exports.unbind_outside_event = function (event_name, namespace) {
    $(document).off(event_name + `.` + namespace + `.` + `OUTSIDE_EVENT`);
  };

  exports.parseUri = parseUri;

  exports.isURL = isURL;

  // In contrast with isURL, returns true only for strings beginning with an
  // http/https protocol.
  exports.is_http_uri = function (str) {
    return typeof str === `string` && str.indexOf(` `) === -1 && /^https?:\/\//.test(str);
  };

  exports.sanitize = function (data) {
    if (typeof $ !== `undefined`) {
      return $(`<div/>`).text(data).html();
    } else {
      return data.replace(/&/g, `&amp;`).replace(/>/g, `&gt;`).replace(/</g, `&lt;`);
    }
  };

  exports.percentify = function (val, total) {
    return total > 0 ? (val / total) * 100 : 0;
  };

  exports.percentage_to_int = function (percentage) {
    // given a percentage string (like css), return int

    const re = new RegExp(`(\\d{0,3})\\s*%`);
    const matches = re.exec(percentage);

    const num = matches[1];
    // first element is the full re match, first element is first group

    return parseInt(num, 10);
  };

  exports.px_to_int = function (pixels) {
    let num = parseInt(pixels, 10);
    if (isNaN(num)) {
      num = 0;
    }
    return num;
  };

  exports.http_build_query = function (formdata, arg_separator) {
    let key,
      use_val,
      use_key,
      i = 0;
    const tmp_arr = [];

    if (!arg_separator) {
      arg_separator = `&`;
    }

    for (key in formdata) {
      if (key) {
        use_val = encodeURIComponent(formdata[key].toString());
        use_key = encodeURIComponent(key);
        tmp_arr[i++] = use_key + `=` + use_val;
      }
    }

    return tmp_arr.join(arg_separator);
  };

  // CAVEAT CALLOR-
  // 1) Have you tried styling the parent element with "text-overflow: ellipsis" ?
  // 2) Have you considered that the outgoing string may be three chars longer than len?
  exports.shorten_string = function (string, len) {
    if (!len) {
      len = 20;
    }
    if (string.length > len) {
      return string.substring(0, len) + `...`;
    } else {
      return string;
    }
  };

  // truncates the string around the middle as opposed to lopping off
  // information at the end
  exports.truncate_middle = function (string, len) {
    if (string) {
      if (len <= 3) {
        return string.substr(0, len);
      } else if (string.length <= len) {
        return string;
      } else {
        const start = (len - 3) / 2;
        const del = string.length - len + 3; //delete this many characters
        return _.splice(string, start, del, `...`);
      }
    }
    return string;
  };

  exports.truncate_middle_html = function ($parent_element, len) {
    const text = $parent_element.text();
    let excess = text.length - len;
    if (excess > 0) {
      const truncate_start = len / 2;
      const truncate_end = text.length - truncate_start + 3; // to account for '...'
      let current_length = 0;
      let truncated = false;

      _.each($parent_element.contents(), function (el) {
        const $el = $(el);
        let el_text = $el.text();
        const el_length = el_text.length;
        if (excess === 0) {
          // everything needed to be cut is cut
          return;
        }
        if (current_length < truncate_start && el_length > truncate_start - current_length) {
          // part of element text needs to be cut off to cut off overall text at truncate_start
          $el.text(el_text.substr(0, truncate_start - current_length) + `...`);
          truncated = true;
          excess -= el_length - (truncate_start - current_length);
        } else if (truncate_start <= current_length && current_length < truncate_end) {
          // element should either be cut off, or part of it saved
          // set remaining excess text accordingly
          if (el_length >= excess) {
            el_text = truncated ? el_text.substr(excess) : `...` + el_text.substr(excess);
            $el.text(el_text.substr(excess));
            excess = 0;
          } else {
            $el.remove();
            excess -= el_length;
          }
        }
        current_length += el_length;
      });
    }
    return $parent_element;
  };

  // trims the protocol, then truncates at the end if necessary
  exports.truncate_url = function (url, len) {
    const a = document.createElement(`a`);
    a.href = url;
    let trimmed = [a.host, a.pathname, a.search, a.hash].join(``);
    if (trimmed.length > len) {
      trimmed = mp.utility.shorten_string(trimmed, len - 3);
    }
    return trimmed;
  };

  exports.commaize = function (number, no_conversion) {
    switch (typeof number) {
      case `number`:
        if (_.isNaN(number)) {
          return number;
        }
        number = number.toString();
        break;
      case `string`:
        break;
      default:
        return number;
    }

    let neg = false;
    if (number[0] === `-`) {
      neg = true;
      number = number.slice(1);
    }

    // Parse main number
    const split = number.split(`.`);
    let commaized = no_conversion ? split[0] : parseInt(split[0] || 0, 10).toString();

    if (commaized.length) {
      commaized = commaized.split(``).reverse().join(``);
      commaized = commaized.match(/.{1,3}/g).join(`,`);
      commaized = commaized.split(``).reverse().join(``);
    }

    if (split[1]) {
      // Add decimals, if applicable
      commaized += `.` + split[1];
    }
    if (neg) {
      commaized = `-` + commaized;
    }
    return commaized;
  };

  exports.pluralize = function (singular, number, plural) {
    plural = plural || singular + `s`;
    return number === 0 || number > 1 ? plural : singular;
  };

  exports.pretty_number = function (n, precision) {
    n = parseFloat(n);
    precision = precision === undefined ? 1 : precision;
    const large_numbers = [
      [Math.pow(10, 15), `Q`],
      [Math.pow(10, 12), `T`],
      [Math.pow(10, 9), `B`],
      [Math.pow(10, 6), `M`],
      [Math.pow(10, 3), `K`],
    ];
    let suffix = ``;

    for (let i = 0; i < large_numbers.length; i++) {
      const bignum = large_numbers[i][0],
        letter = large_numbers[i][1];
      if (Math.abs(n) >= bignum) {
        // @ts-ignore
        n /= bignum;
        // @ts-ignore
        suffix = letter;
        break;
      }
    }
    return exports.pretty_number_raw(n, precision) + suffix;
  };

  exports.pretty_number_raw = function (n, precision) {
    n = parseFloat(n);
    precision = precision === undefined ? 1 : precision;
    const is_negative = n < 0,
      fixed = n.toFixed(precision).split(`.`);

    let formatted = exports.commaize(Math.abs(parseInt(fixed[0], 10)));
    if (fixed[1] && parseInt(fixed[1], 10) !== 0) {
      formatted += `.` + fixed[1];
    }
    return (is_negative ? `-` : ``) + formatted;
  };

  exports.pretty_ratios = function (n, precision) {
    n = parseFloat(n);
    precision = precision === undefined ? 2 : precision;
    return n.toFixed(precision);
  };

  exports.pretty_file_size = pretty_file_size;

  // takes a number and returns an ordinalized string:
  //
  // 1 -> "1st"
  // 45 -> "45th"
  exports.ordinalize = function (number) {
    const abs_number = Math.abs(number);
    if (_.include([11, 12, 13], abs_number % 100)) {
      return number + `th`;
    }

    switch (abs_number % 10) {
      case 1:
        return number + `st`;
      case 2:
        return number + `nd`;
      case 3:
        return number + `rd`;
      default:
        return number + `th`;
    }
  };

  exports.format_numeric_bucket = function (bucket) {
    const range = bucket.split(` - `);
    return exports.pretty_number(range[0]) + ` - ` + exports.pretty_number(range[1]);
  };

  exports.expand_num = function (num) {
    num = num.replace(/,/g, ``);
    const n = num.slice(0, num.length - 1);
    if (/.*M$/.test(num)) {
      return parseFloat(n) * 1000000;
    } else if (/.*K$/.test(num)) {
      return parseFloat(n) * 1000;
    } else {
      return parseFloat(num);
    }
  };

  // This is not a true deep copy, it will not copy arrays or contents of arrays
  exports.deep_copy = function () {
    const result = {},
      properties = {};
    let i, p, objects;
    for (i = arguments.length - 1; i >= 0; i--) {
      if (typeof arguments[i] !== `undefined`) {
        if (arguments[i] !== null && typeof arguments[i] === `object` && !_.isArray(arguments[i])) {
          for (p in arguments[i]) {
            if (Object.prototype.hasOwnProperty.call(arguments[i], p)) {
              properties[p] = true;
            }
          }
        } else {
          return arguments[i];
        }
      }
    }
    for (p in properties) {
      if (Object.prototype.hasOwnProperty.call(properties, p)) {
        objects = [];
        for (i = 0; i < arguments.length; i++) {
          if (typeof arguments[i] !== `undefined`) {
            objects.push(arguments[i][p]);
          }
        }
        result[p] = exports.deep_copy.apply(this, objects);
      }
    }
    return result;
  };

  exports.floatformat = function (num, places) {
    // warning: using this + commaize does not do what you expect
    // when your numbers are in scientific notation
    if (typeof num === `number`) {
      const n = num.toFixed(places);
      const intn = parseInt(n, 10);
      // @ts-ignore
      if (n - intn === 0) {
        return intn;
      } else {
        return n;
      }
    } else {
      return num;
    }
  };

  exports.default_date_format = `yyyy-MM-dd`;

  exports.to_localized_date = function (date) {
    if (_.isNaN(mp.report.globals.utc_offset)) {
      throw new Error(`utc_offset not set`);
    }

    return new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds(),
      // TODO: refactor to not use datejs which overrides native Date
      // @ts-ignore 'addMinutes' does not exist on type 'Date'.
    ).addMinutes(mp.report.globals.utc_offset);
  };

  exports.to_date_string = function (date) {
    return date.toString(this.default_date_format);
  };

  exports.to_localized_date_string = function (date) {
    return mp.utility.to_localized_date(date).toString(this.default_date_format);
  };

  // converts a string date of the form yyyy-mm-dd or yyyy-mm-dd hh:mm:ss to the ms epoch
  exports.date_to_ms_epoch = function (date_string) {
    let time_split;
    const split = date_string.split(` `);
    const date_split = split[0].split(`-`);
    if (split[1]) {
      time_split = split[1].split(`:`);
    }

    // Strip off leading 0 from the month, since this messes up the parseInt.
    if (date_split[1][0] === `0`) {
      date_split[1] = date_split[1][1];
    }

    // Move one month back since js Date is 0-11, and returned date is 1-12
    if (time_split) {
      return new Date(
        date_split[0],
        parseInt(date_split[1], 10) - 1,
        date_split[2],
        time_split[0],
        time_split[1],
        time_split[2],
      ).getTime();
    } else {
      return new Date(date_split[0], parseInt(date_split[1], 10) - 1, date_split[2]).getTime();
    }
  };

  exports.MP_ANYTHING_EVENT = `$mp_anything_event`;

  exports.prepare_event_names_for_dropdown = function (events) {
    events = _(events)
      .chain()
      .map((ev) => [ev, mp.utility.rename_mp_event(ev)])
      .sortBy((ev) => ev[1].toLowerCase())
      .value();
    return events;
  };

  exports.rename_mp_event = function (event_name) {
    if (typeof Backbone !== `undefined` && Backbone.data && Backbone.data.custom_events) {
      const custom_events = Backbone.data.custom_events;
      const deleted_custom_events = Backbone.data.deleted_custom_events;
      if (custom_events.is_custom_event(event_name)) {
        let custom_event = custom_events.get_by_name(event_name);
        const deleted = !custom_event;
        if (deleted) {
          custom_event = deleted_custom_events.get_by_name(event_name);
        }
        if (custom_event) {
          return custom_event.get_display_name();
        } else {
          return event_name;
        }
      }
    }

    return displayNameRegistry.getEventDisplayName(event_name);
  };

  exports.mp_property_inverse = function () {
    return _.invert(mp.report.globals.mapped_properties);
  };

  exports.rename_mp_property = rename_mp_property;

  exports.get_human_readable_param_list = function (params) {
    // Property can either be an object or a string (for legacy support)
    let property = params.property;
    if (typeof property === `object`) {
      property = property.name;
    }
    property = exports.rename_mp_property(property);
    const filter = params.filter;

    let operator, num1, num2, result;
    switch (params.type) {
      case `distinct_id`:
      case `boolean`:
        return [
          {val: property, type: `property`},
          {val: `is`, type: `operator`},
          {val: filter.operand, type: `operand`},
        ];
      case `behavioral`:
        if (filter.operator === `><`) {
          num1 = parseFloat(filter.operand[0]);
          num2 = parseFloat(filter.operand[1]);
          result = [
            {val: property, type: `property`},
            {val: `is between`, type: `operator`},
            {val: Math.min(num1, num2), type: `operand`},
            {val: `and`, type: `operator`},
            {val: Math.max(num1, num2), type: `operand`},
          ];
        } else {
          if (filter.operator === `<`) {
            operator = `less than`;
          } else if (filter.operator === `>`) {
            operator = `greater than`;
          } else if (filter.operator === `==`) {
            operator = `exactly`;
          }
          result = [
            {val: property, type: `property`},
            {val: operator, type: `operator`},
            {val: parseFloat(filter.operand), type: `operand`},
          ];
        }
        return result;
      case `number`:
        if (filter.operator === `><`) {
          num1 = parseFloat(filter.operand[0]);
          num2 = parseFloat(filter.operand[1]);
          result = [
            {val: property, type: `property`},
            {val: `is between`, type: `operator`},
            {val: Math.min(num1, num2), type: `operand`},
            {val: `and`, type: `operator`},
            {val: Math.max(num1, num2), type: `operand`},
          ];
        } else {
          if (filter.operator === `<`) {
            operator = `is less than`;
          } else if (filter.operator === `>`) {
            operator = `is greater than`;
          } else if (filter.operator === `==`) {
            operator = `equals`;
          }
          result = [
            {val: property, type: `property`},
            {val: operator, type: `operator`},
            {val: parseFloat(filter.operand), type: `operand`},
          ];
        }
        return result;
      case `datetime`:
        if (filter.operator === `==`) {
          return [
            {val: property, type: `property`},
            {val: `occured on`, type: `operator`},
            {val: filter.operand, type: `operand`},
          ];
        } else if (filter.operator === `><`) {
          return [
            {val: property, type: `property`},
            {val: `occured between`, type: `operator`},
            {val: filter.operand[0], type: `operand`},
            {val: `and`, type: `operator`},
            {val: filter.operand[1], type: `operand`},
          ];
        } else {
          // Special cases to turn "1 days" to "1 day ago"
          let unit = filter.unit;
          if (filter.operand === 1) {
            unit = unit.substring(0, unit.length - 1);
          }
          unit += ` ago`;

          return [
            {val: property, type: `property`},
            {val: filter.operator === `<` ? `was less than` : `was more than`, type: `operator`},
            {val: filter.operand, type: `operand`},
            {val: unit, type: `unit`},
          ];
        }
      case `list`:
        return [
          {val: filter.operand, type: `operand`},
          {val: filter.operator === `in` ? `is in` : `is not in`, type: `operator`},
          {val: property, type: `property`},
        ];
      case `object`:
        // not an option from property filter UI... don't think this case is applicable
        return [];
      case `unknown`:
      case `string`:
        if (filter.operator === `in` || filter.operator === `not in`) {
          return [
            {val: property, type: `property`},
            {val: filter.operator === `in` ? `contains` : `does not contain`, type: `operator`},
            {
              val: property === `Country` ? mp.utility.get_country_name(filter.operand) : filter.operand,
              type: `operand`,
            },
          ];
        } else if (filter.operator === `set`) {
          return [
            {val: property, type: `property`},
            {val: `is set`, type: `operator`},
          ];
        } else if (filter.operator === `not set`) {
          return [
            {val: property, type: `property`},
            {val: `is not set`, type: `operator`},
          ];
        } else {
          // filter.operator is either '==' or '!='
          let separator;
          if (filter.operand.length > 1) {
            if (filter.operator === `==`) {
              operator = `is either`;
              separator = `or`;
            } else {
              operator = `is neither`;
              separator = `nor`;
            }
          } else {
            operator = filter.operator === `==` ? `is` : `is not`;
          }
          return [
            {val: property, type: `property`},
            {val: operator, type: `operator`},
            {
              val: property === `Country` ? _.map(filter.operand, mp.utility.get_country_name) : filter.operand,
              type: `operand_list`,
              separator,
            },
          ];
        }
      default:
        throw Error(`Unexpected property type "${params.type}"`);
    }
  };

  exports.get_property_filter_clause_text = function (param_list) {
    // takes output from get_human_readable_param_list to return
    // a human readable string (for example, for a tooltip)
    // e.g., [{type: 'property', val: 'project_id'},
    //        {type: 'operator', val: 'is greater than'},
    //        {type: 'operand', val: 3}]
    // -> 'project_id is greater than 3'
    let text = ``;
    _.each(param_list, function (elem, i) {
      if (i !== 0) {
        text += ` `;
      }
      if (elem.type !== `operand_list`) {
        text += elem.val;
      } else {
        const operands = elem.val;
        if (operands.length < 2) {
          text += operands;
        } else {
          const separator = operands.length === 2 ? ` ` + elem.separator + ` ` : `, `;
          _.each(operands, function (operand, i) {
            if (i === operands.length - 1 && operands.length > 2) {
              text += elem.separator + ` `;
            }
            text += operand;
            if (i !== operands.length - 1) {
              text += separator;
            }
          });
        }
      }
    });
    return text;
  };

  exports.to_mp_property = function (property_name) {
    const mapping = exports.mp_property_inverse;
    if (Object.prototype.hasOwnProperty.call(mapping, property_name)) {
      property_name = mapping[property_name];
    }
    return property_name;
  };

  exports.format_long_value = function (_property_name, property_value) {
    let ret = property_value;
    // TODO: refactor to not use datejs which overrides native Date
    // @ts-ignore 'parseExact' does not exist on type 'Date'.
    const found = Date.parseExact(ret, `yyyy-MM-ddTHH:mm:ss`);
    if (found) {
      ret = found.toString(`ddd, MMM d, hh:mm tt`);
    }
    return _.trim(ret);
  };

  exports.make_tag = function (name, attrs, text) {
    return $(`<` + name + `></` + name + `>`, attrs).text(text);
  };

  exports.format_html_property_value = function (property_name, property_value, max_length) {
    let long_value;
    let ret_tag;

    if (_.isUndefined(property_value)) {
      ret_tag = $(`<span>&nbsp;</span>`);
    } else {
      if (_.isObject(property_value)) {
        property_value = JSON.stringify(property_value);
      }
      const prop_value = String(property_value);

      if (property_name && (property_name.match(/^\$?email$/i) || exports.is_valid_email(property_value))) {
        long_value = exports.format_long_value(property_name, property_value);
        ret_tag = exports.make_tag(
          `a`,
          {class: `email_property_link`, title: long_value, href: `mailto:` + prop_value},
          prop_value,
        );
      } else if (exports.isURL(property_value)) {
        const shortened = max_length ? exports.truncate_middle(prop_value, max_length) : prop_value;
        let href_val = prop_value;
        if (href_val.indexOf(`://`) === -1) {
          href_val = `http://` + href_val;
        }
        ret_tag = exports.make_tag(
          `a`,
          {
            class: `url_property_link`,
            target: `_blank`,
            href: href_val,
            title: shortened !== prop_value ? prop_value : undefined,
          },
          shortened,
        );
      } else {
        long_value = exports.format_long_value(property_name, property_value);
        const title = long_value && max_length && long_value.length > max_length ? long_value : undefined;
        ret_tag = exports.make_tag(`div`, {title}, prop_value);
      }
    }
    ret_tag.addClass(`mp_tooltip_enabled`);
    return ret_tag;
  };

  exports.includes_campaign_event = includes_campaign_event;

  exports.includes_experiment_event = function (events) {
    return includes_event_type(events, is_experiment_event);
  };

  exports.get_campaign_name = function (campaignID) {
    // find campaigns where first element of array matches id
    const campaign = _.findWhere(mp.report.globals.project_campaigns, {0: parseInt(campaignID, 10)});
    return campaign ? campaign[1] : campaignID.toString();
  };

  const fetch_mapping = function (mapping) {
    let baseUrl = `/api/app`;
    if (mp.report.globals.workspace_id) {
      baseUrl += `/workspaces/${mp.report.globals.workspace_id}`;
    } else {
      baseUrl += `/projects/${mp.report.globals.project_id}`;
    }
    const mappingUrls = {
      experiments: `${baseUrl}/experiments/`,
      message_variants: `${baseUrl}/messages/variants/`,
    };
    mp.report.globals[mapping] = [];
    return apiClient.get(mappingUrls[mapping]).then((results) => {
      mp.report.globals[mapping] = results;
    });
  };

  exports.fetch_message_variants = function () {
    return fetch_mapping(`message_variants`);
  };

  exports.fetch_experiments = function () {
    return fetch_mapping(`experiments`);
  };

  exports.get_experiment_name = function (experimentIdString) {
    const experimentID = parseInt(experimentIdString, 10);
    const experiment = (mp.report.globals.experiments || []).find((exp) => exp.id === experimentID);
    return experiment ? experiment.name : String(experimentID);
  };

  exports.get_experiment_variant_name = function (experimentVariantIdString) {
    const variantID = parseInt(experimentVariantIdString, 10);
    const experiment = (mp.report.globals.experiments || []).find((exp) =>
      exp.variants.some((variant) => variant.id === variantID),
    );
    if (experiment) {
      const variantIndex = experiment.variants
        .map((variant) => variant.id)
        .sort((a, b) => a - b)
        .indexOf(variantID);
      return variantIndex === 0
        ? `Original variant - ${experiment.name}`
        : `Variant ${variantIndex} - ${experiment.name}`;
    }

    return String(variantID);
  };

  exports.get_variant_name = function (messageIdString) {
    const messageID = parseInt(messageIdString, 10);
    const message = (mp.report.globals.message_variants || []).find((m) => m.variants.includes(messageID));
    const variantIndex = message ? message.variants.indexOf(messageID) : null;
    let name;
    if (variantIndex !== null) {
      name = variantIndex === 0 ? `Original variant - ${message.name}` : `Variant ${variantIndex} - ${message.name}`;
    } else {
      name = messageIdString;
    }

    return name;
  };

  exports.get_country_name = function (country_code) {
    const cc = exports.country_codes,
      uc_code = country_code.toUpperCase();
    return Object.prototype.hasOwnProperty.call(cc, uc_code) ? cc[uc_code] : country_code;
  };

  const HTTP_PROPS = [`$initial_referring_domain`, `$referring_domain`, `origin_domain`, `initial domain`];
  exports.human_name_for_segment_value = function (segment_value, segment_property, arb_event) {
    // handle both p+e properties and old-style properties
    const property = _.isObject(segment_property) ? segment_property.name : segment_property;

    if (property === `mp_country_code`) {
      segment_value = exports.get_country_name(segment_value);
    } else if (_.contains(HTTP_PROPS, segment_property)) {
      if (exports.isURL(segment_value) && !/^http:\/\//.test(segment_value)) {
        segment_value = `http://` + segment_value;
      }
    } else if (property === `campaign_id` && exports.includes_campaign_event([arb_event])) {
      segment_value = exports.get_campaign_name(segment_value);
    } else if (property === `message_id` && exports.includes_campaign_event([arb_event])) {
      segment_value = exports.get_variant_name(segment_value);
    } else if (property === `$experiment_id` && exports.includes_experiment_event([arb_event])) {
      segment_value = exports.get_experiment_name(segment_value);
    } else if (property === `$variant_id` && exports.includes_experiment_event([arb_event])) {
      segment_value = exports.get_experiment_variant_name(segment_value);
    }

    return segment_value;
  };

  exports.log =
    mp.globals && mp.globals.debug && window.console && console.log && console.log.apply
      ? _.bind(console.log, console)
      : function () {};
  exports.warn =
    mp.globals && mp.globals.debug && window.console && console.warn && console.warn.apply
      ? _.bind(console.warn, console)
      : function () {};
  exports.error =
    mp.globals && mp.globals.debug && window.console && console.error && console.error.apply
      ? _.bind(console.error, console)
      : function () {};

  exports.group =
    mp.globals && mp.globals.debug && window.console && console.group && console.group.apply
      ? _.bind(console.group, console)
      : exports.log;
  exports.groupCollapsed =
    mp.globals && mp.globals.debug && window.console && console.groupCollapsed && console.groupCollapsed.apply
      ? _.bind(console.groupCollapsed, console)
      : exports.log;
  exports.groupEnd =
    mp.globals && mp.globals.debug && window.console && console.groupEnd && console.groupEnd.apply
      ? _.bind(console.groupEnd, console)
      : function () {};

  exports.timer = {
    start_times: {},

    start(id) {
      id = id || Math.random();
      this.start_times[id] = new Date().getTime();
      return id;
    },

    stop(id) {
      const diff = new Date().getTime() - this.start_times[id];
      delete this.start_times[id];
      return diff;
    },
  };

  // RIPPED FROM streams.util
  exports.string_time = function (seconds) {
    const minutes = seconds / 60,
      hours = minutes / 60,
      days = hours / 24,
      weeks = days / 7;

    if (isNaN(seconds)) {
      return `?`;
    } else if (seconds < 60) {
      return `less than a minute`;
    } else if (minutes < 90) {
      return minutes.toFixed(0) + ` min.`;
    } else if (hours < 36) {
      return hours.toFixed(0) + ` hours`;
    } else if (days < 11) {
      return days.toFixed(0) + ` days`;
    } else {
      return weeks.toFixed(0) + ` weeks`;
    }
  };

  exports.time_since_date = function (date) {
    // @ts-ignore
    const seconds = (new Date() - date) / 1000;
    return exports.string_time(seconds);
  };

  exports.inflect = function (noun, number) {
    if (typeof number === `string`) {
      number = parseFloat(number);
    }
    let inflected = noun;
    if (number !== 1) {
      const replacements = [
        [/(s|ss|sh|ch|x|o)$/, `$1es`],
        [/([^aeiou])y$/, `$1ies`],
        [/$/, `s`],
      ];
      for (let i = 0; i < replacements.length; i++) {
        if (inflected.match(replacements[i][0])) {
          inflected = inflected.replace(replacements[i][0], replacements[i][1]);
          break;
        }
      }
    }
    return inflected;
  };

  /**
   * Takes in an integer number of seconds and returns a dict
   * consisting of human readable attributes for the duration
   *
   * @param {number} seconds - length of duration in seconds
   * @param {number} precision - number of decimal places to round formatted duration to, default 1
   *
   * @example get_readable_duration(150)
   *   {duration: 2.5, timeUnit: `minute`}
   */
  exports.get_readable_duration = function (seconds, precision = 1) {
    let duration = 0;
    let timeUnit = `second`;
    if (seconds < 60) {
      duration = seconds;
      timeUnit = `second`;
    } else if (seconds < 60 * 60) {
      duration = Math.floor(seconds / 60);
      timeUnit = `minute`;
    } else if (seconds < 60 * 60 * 24) {
      // @ts-ignore
      duration = (seconds / 60 / 60).toFixed(precision);
      timeUnit = `hour`;
    } else {
      // @ts-ignore
      duration = (seconds / 60 / 60 / 24).toFixed(precision);
      timeUnit = `day`;
    }
    return {duration, timeUnit};
  };

  /**
   * Takes in an integer number of seconds and returns a human readable string for the duration
   * e.g. 43 seconds, 5 minutes, 2 hours, 3 days
   *
   * @param {number} seconds - length of duration in seconds
   * @param {number} precision - number of decimal places to round formatted duration to, default 1
   * @returns {string} formatted duration string
   */
  exports.format_duration = function (seconds, precision = 1) {
    const {duration, timeUnit} = mp.utility.get_readable_duration(seconds, precision);
    return `${duration} ${mp.utility.inflect(timeUnit, duration)}`;
  };

  exports.throttle = function (func, wait, context) {
    // like _.debounce (with code stolen from the same), but fires off the first call immediately
    let timeout;
    return function () {
      // @ts-ignore - An outer value of 'this' is shadowed by this container.
      const inner_context = context || this,
        args = arguments;
      const clear = function () {
        timeout = null;
      };
      const throttler = function () {
        clear();
        func.apply(inner_context, args);
      };
      clearTimeout(timeout);
      if (timeout) {
        timeout = setTimeout(throttler, wait);
      } else {
        func.apply(inner_context, args);
        timeout = setTimeout(clear, wait);
      }
    };
  };

  exports.strip_spaces_between_tags = function (html_str) {
    return html_str.replace(/>\s+</g, `><`);
  };

  // Given a $('<table>') or a $('<tr>'), returns a
  // $('<tr>') that is one pixel tall and has
  // the same column widths as the argument.
  //
  // This is mostly useful for making tables that
  // have stationary headers and scrolling bodies, or vice versa.
  exports.spacer_row = function (table) {
    const ret = $(`<tr class="spacer_row" style="height: 1px"></tr>`);
    let master_row = $(table);
    if (!master_row.is(`tr`)) {
      master_row = table.find(`tr`).first();
    }
    const master_cells = master_row.find(`th, td`);
    master_cells.each(function (_index, elem) {
      const width = $(elem).outerWidth();
      const span = $(elem).attr(`colspan`);

      const spacer_cell = $(`<td></td>`).html(
        // @ts-ignore
        $(`<div></div>`).css(`width`, width + `px`),
      );

      if (span) {
        spacer_cell.attr(`colspan`, span);
      }

      ret.append(spacer_cell);
    });

    return ret;
  };

  // MATH FUNCTIONS

  // rounds a number to a certain amount of decimal places
  exports.round = function (num, dec) {
    return Math.round(num * Math.pow(10, dec)) / Math.pow(10, dec);
  };

  // keep in sync with chart colors in iron/common/util/colors.js
  const COLORS = [
    `#7856FF`,
    `#FF7557`,
    `#80E1D9`,
    `#F8BC3B`,
    `#B2596E`,
    `#72BEF4`,
    `#FFB27A`,
    `#5E5CB6`,
    `#3BA974`,
    `#FEBBB2`,
    `#CA80DC`,
    `#5BB7AF`,
  ];

  // @ts-ignore
  exports.nth_color = function (index, color_scheme) {
    return COLORS[index % COLORS.length];
  };

  // same implementation as google closure
  exports.regex_escape = function (s) {
    return String(s)
      .replace(/([-()[\]{}+?*.$^|,:#<!\\])/g, `\\$1`)
      .replace(new RegExp(`\\x08`, `g`), `\\x08`);
  };

  exports.base64_encode = function (data) {
    const b64 = `ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=`;
    let o1,
      o2,
      o3,
      h1,
      h2,
      h3,
      h4,
      bits,
      i = 0,
      ac = 0,
      enc = ``;
    const tmp_arr = [];

    if (!data) {
      return data;
    }

    data = exports.utf8_encode(data);

    do {
      // pack three octets into four hexets
      o1 = data.charCodeAt(i++);
      o2 = data.charCodeAt(i++);
      o3 = data.charCodeAt(i++);

      bits = (o1 << 16) | (o2 << 8) | o3;

      h1 = (bits >> 18) & 0x3f;
      h2 = (bits >> 12) & 0x3f;
      h3 = (bits >> 6) & 0x3f;
      h4 = bits & 0x3f;

      // use hexets to index into b64, and append result to encoded string
      tmp_arr[ac++] = b64.charAt(h1) + b64.charAt(h2) + b64.charAt(h3) + b64.charAt(h4);
    } while (i < data.length);

    enc = tmp_arr.join(``);

    switch (data.length % 3) {
      case 1:
        enc = enc.slice(0, -2) + `==`;
        break;
      case 2:
        enc = enc.slice(0, -1) + `=`;
        break;
    }

    return enc;
  };

  exports.utf8_encode = function (string) {
    string = (string + ``).replace(/\r\n/g, `\n`).replace(/\r/g, `\n`);

    let utftext = ``,
      start,
      end;
    let stringl = 0,
      n;

    start = end = 0;
    stringl = string.length;

    for (n = 0; n < stringl; n++) {
      const c1 = string.charCodeAt(n);
      let enc = null;

      if (c1 < 128) {
        end++;
      } else if (c1 > 127 && c1 < 2048) {
        enc = String.fromCharCode((c1 >> 6) | 192, (c1 & 63) | 128);
      } else {
        enc = String.fromCharCode((c1 >> 12) | 224, ((c1 >> 6) & 63) | 128, (c1 & 63) | 128);
      }
      if (enc !== null) {
        if (end > start) {
          utftext += string.substring(start, end);
        }
        utftext += enc;
        start = end = n + 1;
      }
    }

    if (end > start) {
      utftext += string.substring(start, string.length);
    }

    return utftext;
  };

  exports.get_next_month = function (date) {
    const next_month = (date.getUTCMonth() + 1) % 12;
    const next_year = date.getUTCFullYear() + Math.floor((date.getUTCMonth() + 1) / 12);
    const new_date = new Date(next_year, next_month, 1);
    // TODO: refactor to not use datejs which overrides native Date
    // @ts-ignore 'addMinutes' does not exist on type 'Date'.
    return new_date.addMinutes(-new_date.getTimezoneOffset());
  };

  exports.first_of_month = function (date) {
    const new_date = new Date(date.getUTCFullYear(), date.getUTCMonth(), 1);
    // TODO: refactor to not use datejs which overrides native Date
    // @ts-ignore 'addMinutes' does not exist on type 'Date'.
    return new_date.addMinutes(-new_date.getTimezoneOffset());
  };

  exports.get_people_events_or_standard_property_filter_class = function () {
    return mp.widgets.PeopleEventsPropertyFilter;
  };

  exports.get_people_events_or_standard_property_select_class = function () {
    return Backbone.widgets.PeopleEventsPropertySelect;
  };

  const HIDDEN_PEOPLE_PROPERTIES = [
    `$answers`,
    `$campaigns`,
    `$deliveries`,
    `$experiments`,
    `$events_distinct_id`,
    `$mp_message_sends`,
    `$mp_visual_journeys`,
    `$notifications`,
  ];

  exports.get_global_hidden_people_properties = function () {
    return _.clone(HIDDEN_PEOPLE_PROPERTIES);
  };

  exports.CSS_TRANSITION_END_EVENTS = `transitionend webkitTransitionEnd oTransitionEnd MSTransitionEnd`;

  exports.parse_selector = parse_selector;

  exports.build_selector = build_selector;

  // Use conjunction to combine all the clauses in clauses
  exports.cohort_treeify = function (conjunction, clauses) {
    if (clauses.length === 1) {
      return clauses[0];
    } else {
      const half = Math.ceil(clauses.length / 2);
      return {
        operator: conjunction,
        children: [
          exports.cohort_treeify(conjunction, clauses.slice(0, half)),
          exports.cohort_treeify(conjunction, clauses.slice(half)),
        ],
      };
    }
  };

  exports.section_viewed_tracking_helper = function (tracking_info) {
    const window_top = $(window).scrollTop();
    const window_bottom = window_top + $(window).height();
    const section_height = tracking_info.$selector.height();
    const section_top = tracking_info.$selector.position().top;
    const section_start_top = section_height / 4 + section_top;
    const section_end_top = (section_height / 4) * 3 + section_top;

    if (window_top <= section_end_top && window_bottom > section_start_top && !tracking_info.was_on_screen) {
      tracking_info.was_on_screen = true;

      mixpanel.time_event(tracking_info.event_name);
    } else if ((window_top > section_end_top || window_bottom < section_start_top) && tracking_info.was_on_screen) {
      tracking_info.was_on_screen = false;
      mixpanel.track(tracking_info.event_name);
    }
  };

  exports.get_timezone_dropdown_options = function () {
    return mp.report.globals.timezones.map(function (timezone) {
      return [timezone[0], timezone[1]];
    });
  };

  exports.progressive_scroll_handler = function ({
    position, // current progressive scroll position
    list_size, // progressive scroll initial list size
    max_size, // progressive scroll maximum list size
    total_num_items, // total number of items in the list
    get_item_height, // getter for height (px) of an item in the list
    update_position_func, // function that will be called with `new_position` and `new_scroll args calculated by this function
    scroll_buffer_px = 250, // distance user needs to scroll near top/bottom of list
    // to trigger a progressive list size update
  }) {
    let rendering = false;
    return function (ev) {
      const scroll_top = ev.target.scrollTop;
      const scroll_bottom = scroll_top + ev.target.offsetHeight;
      const item_height = get_item_height();
      // if user has scrolled near the bottom and we haven't rendered all the items,
      // increase progressive position (add items at bottom and remove from top)
      const increase_size = scroll_bottom + scroll_buffer_px >= ev.target.scrollHeight && position < total_num_items;
      // if user has scrolled near the top and we're outside the progessive max
      // (i.e. we've removed items from the top of the list)
      // decrease progressive position (remove items at bottom and add at top)
      const decrease_size = scroll_top <= scroll_buffer_px && position > max_size;
      if (!rendering && (increase_size || decrease_size)) {
        rendering = true;
        window.requestAnimationFrame(() => {
          const size_delta = list_size * (decrease_size ? -1 : 1);
          const new_position = Math.max(position + size_delta, list_size);
          let new_scroll;

          if (new_position !== position) {
            // if we're ever adding or removing items from the top of the list,
            // need to adjust scroll position so the user says at the same place
            // in the list relative to the items - scroll up/down cases below:
            // scrolling up
            if (new_position < position && position > max_size) {
              new_scroll = item_height * list_size;
              // scrolling down
            } else if (new_position > position && new_position > max_size) {
              new_scroll = scroll_top - item_height * list_size;
            }
            update_position_func(new_position, new_scroll);
          }
          rendering = false;
        });
      }
    };
  };

  exports.get_country_dropdown_options = getCountryDropdownOptions;

  exports.save_csv_as_file = function (str, title) {
    const blob = new Blob([str], {type: `text/csv`});
    return this.save_blob_as_file(blob, title, `csv`);
  };

  exports.save_blob_as_file = function (blob, title, format) {
    if ([`image/png`, `application/pdf`, `text/csv`].includes(blob.type)) {
      const blobURL = URL.createObjectURL(blob);
      // launch named download via hidden link
      const link = document.createElement(`a`);
      link.style.display = `none`;
      link.href = blobURL;
      link.download = `${title}.${format.toLowerCase()}`;
      document.body.appendChild(link);
      link.click();
      // clean up
      URL.revokeObjectURL(blobURL);
      link.remove();
    }
  };

  // This is a stop gap. Some adblockers are known to break our pricing signup page due to Marketo.
  // TODO: Fix Marketo and make it a reliable flow (then yank this out).
  exports.maybeInjectAdblockWarningBanner = function () {
    const injectAdblockBanner = () => {
      const reportBanner = document.querySelector(`mp-report-message-banner`);
      if (reportBanner) {
        reportBanner.setAttribute(`visible`, `true`);
        reportBanner.setAttribute(
          `message`,
          `If you are having trouble submitting a form, please try disabling your ad blocker.`,
        );
        reportBanner.setAttribute(`type`, `warn`);
        reportBanner.setAttribute(`size`, `small`);
        mixpanel.track(`Pricing - ad block banner shown`);
      }
    };

    setTimeout(() => {
      // @ts-ignore
      if (!window.MktoForms2 || !window.mixpanel.get_distinct_id) {
        // marketo is important for marketing pages
        // since it provides functionality for sales contact forms
        // mixpanel.get_distinct_id is used in signup form
        // some ad blockers block them, so we ought to show an unobtrusive banner
        // informing them that their experience may be degraded due to the adblocker
        injectAdblockBanner();
      }
    }, 5000);
  };

  exports.arb_escape = arbEscape;
  exports.is_valid_email = isValidEmail;

  exports.fetch_event_definitions = fetchEventDefinitions;
  exports.fetch_property_definitions = fetchPropertyDefinitions;
  exports.get_tooltip_data_for_event = getTooltipDataForEvent;
  exports.get_tooltip_data_for_property = getTooltipDataForProperty;

  // non jquery implemention of $(document).ready
  exports.onDocumentReady = onDocumentReady;
});
